import { useContext, useEffect } from 'react';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';

import { ApplicationEventNames } from '~/constants/ApplicationEvents';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { PosthogHelper } from '~/helpers/PosthogHelper';
import { DepositTransferModalKey, UrlModalHelper } from '~/helpers/UrlModalHelper';
import { AnalyticsEventNames } from '~/tracking';
import { formatCurrency } from '~/utils/currencyFormatter';
import { normalizePaymentMethod } from '~/utils/normalizePaymentMethod';
import { Button } from '~/vinovest-design-system/components';
import { IconCheck } from '~/vinovest-design-system/components/Icons';

export const DepositInitiated = () => {
    const router = useRouter();
    const { depositAmount, paymentMethod } = useContext(DepositContext);
    const source = normalizePaymentMethod(paymentMethod);
    const sourceName = `${source.name} ****${source.lastFour}`;
    const depositSource = `${source.type != 'vinovest_funds' ? sourceName : source.name} `;

    const depositDate = dayjs().format('MMM D');
    const bankDepositCompletedDate = source.type === 'card' ? depositDate : dayjs().add(3, 'day').format('MMM D');
    const creditCardDepositCompleteMessage = 'Available to trade, spend now.';
    const bankDepositCompleteMessage = 'Available to trade, spend, withdraw';
    const transferCompleteClass = source.type === 'card' ? 'bg-green-600' : 'bg-neutrals-400';

    useEffect(() => {
        PosthogHelper.captureEvent(AnalyticsEventNames.DepositConfirmation);
    }, []);

    const handleTransferReviewed = async () => {
        window.dispatchEvent(new CustomEvent(ApplicationEventNames.DepositModalClosed));
        UrlModalHelper.closeModal(router, DepositTransferModalKey, true);
    };

    return (
        <div>
            <div className={'bg-gray-200 border-[1px] border-gray-200 rounded-[20px] p-4 md:p-10 mb-8'}>
                <div className={'bg-white border-[1px] border-gray-300 rounded-[20px] p-4 md:p-8'}>
                    <p className={'text-[16px] mb-8'}>
                        +{formatCurrency(depositAmount, false)} from {depositSource}
                    </p>
                    <div
                        className={'pb-12 flex items-start relative border-l-green-600 border-l-[3px] ml-[15px] mb-0.5'}
                    >
                        {source.type === 'card' || source.type === 'vinovest_funds' ? (
                            <>
                                <div
                                    className={`h-4 w-4 inline-block rounded-full absolute -left-[9px] ${transferCompleteClass}`}
                                />
                                <div className={'pl-[25px]'}>
                                    <p className={'text-14'}>
                                        {source.type === 'vinovest_funds' ? 'Transfer' : 'Deposit'} initiated
                                    </p>
                                    <p className={'text-[16px] text-neutrals-600'}>{depositDate} • Today</p>
                                </div>
                            </>
                        ) : (
                            <>
                                <div
                                    className={
                                        'bg-green-600 w-[31px] h-[31px] rounded-full absolute -left-[18px] -top-px flex items-center justify-center'
                                    }
                                >
                                    <IconCheck width={'10px'} height={'10px'} stroke={'#fff'} />
                                </div>
                                <div className={'pl-[25px]'}>
                                    <p className={'text-14'}>
                                        {source.type === 'vinovest_funds' ? 'Transfer' : 'Deposit'} initiated
                                    </p>
                                    <p className={'text-[16px] text-neutrals-600'}>{depositDate} • Today</p>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={'mb-6 flex items-start pl-[8.5px] relative'}>
                        {source.type === 'card' || source.type === 'vinovest_funds' ? (
                            <>
                                <div
                                    className={
                                        'bg-green-600 w-[31px] h-[31px] rounded-full absolute left-[0] flex items-center justify-center'
                                    }
                                >
                                    <IconCheck width={'10px'} height={'10px'} stroke={'#fff'} />
                                </div>
                                <div className={'pl-[34px]'}>
                                    <p className={'text-14'}>
                                        {source.type === 'vinovest_funds' ? 'Transfer' : 'Deposit'} completed
                                    </p>
                                    <p className={'text-[16px] text-neutrals-600'}>
                                        {bankDepositCompletedDate} •{' '}
                                        {source.type === 'card'
                                            ? creditCardDepositCompleteMessage
                                            : bankDepositCompleteMessage}
                                    </p>
                                </div>
                            </>
                        ) : (
                            <>
                                <div
                                    className={`h-4 w-4 inline-block rounded-full mr-[18px] ${transferCompleteClass}`}
                                />
                                <div className={''}>
                                    <p className={'text-14'}>
                                        {source.type === 'vinovest_funds' ? 'Transfer' : 'Deposit'} completed
                                    </p>
                                    <p className={'text-[16px] text-neutrals-600'}>
                                        {bankDepositCompletedDate} •{' '}
                                        {source.type === 'card'
                                            ? creditCardDepositCompleteMessage
                                            : bankDepositCompleteMessage}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                    <p className={'text-[16px] text-neutrals-600 mb-6'}>
                        {source.type === 'card' || source.type === 'vinovest_funds'
                            ? `+${formatCurrency(depositAmount, false)}`
                            : `Please make sure to maintain a balance of ${formatCurrency(
                                  depositAmount,
                                  false
                              )} in your bank account until the funds are deducted to avoid any issues.`}
                    </p>
                </div>
            </div>
            <div className={'flex flex-col'}>
                <Button
                    variant={'platinum'}
                    classnames={'mb-5 text-[16px] mx-auto max-w-[250px] w-full'}
                    onClick={handleTransferReviewed}
                >
                    Done
                </Button>
            </div>
        </div>
    );
};
